import { getLang, initI18n } from "./i18n";
import { pickText, PRODUCT_META } from "./site-data";
import { productMark } from "./product-visual";

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ] as string,
  );
}

function render(): void {
  const lang = getLang();
  const isZh = lang === "zh";
  const root = document.getElementById("advanced-grid");
  if (!root) return;
  root.innerHTML = "";

  for (const p of PRODUCT_META) {
    const focus = p.advancedFocus
      .map((x) => `<li>${escapeHtml(pickText(x, lang))}</li>`)
      .join("");
    const card = document.createElement("article");
    card.className = "feature-card reveal";
    card.dataset.slug = p.slug;
    // 不在这 7 张卡里重复同一张「骨骼与控制器」示意图（七张一模一样，
    // 反而把每款乐器的区别淹掉了）。整页那张流程图已经在页首出现过。
    card.innerHTML = `
      <h3>${productMark(p.key)} <span>${escapeHtml(pickText(p.title, lang))}</span></h3>
      <p class="muted">${isZh ? "七个阶段：预处理骨骼 → 初始化实例 → 控制体系 → 关键姿势 → 乐器几何 → 导出人物数据 → Rust 端注册。" : "Seven stages: rig prep → instance → control system → key poses → instrument geometry → export → register in the Rust app."}</p>
      <ul>${focus}</ul>
      <div class="feature-actions">
        <a class="btn secondary" href="/advanced/${p.slug}.html">${isZh ? "进入进阶教程" : "Open advanced tutorial"}</a>
        <a class="btn link" href="/manuals/blender/${p.slug}.html">${isZh ? "Blender 插件界面说明" : "Blender Plugin UI Guide"}</a>
      </div>
    `;
    root.appendChild(card);
  }
}

const FLOW: { zh: string; en: string; descZh: string; descEn: string }[] = [
  {
    zh: "预处理人物骨骼",
    en: "Prepare the rig",
    descZh: "整理骨骼命名、层级、朝向与手部 / 脚部骨骼，作为后续绑定的基础。",
    descEn: "Clean up bone naming, hierarchy, facing direction and hand/foot bones as the base for everything after.",
  },
  {
    zh: "初始化 Music Doll 实例",
    en: "Initialise the Music Doll instance",
    descZh: "以该骨架创建一名演奏者，建立身体 / 控制器 / 乐器的集合结构并登记身份。",
    descEn: "Create a performer from that armature, building the body / controllers / instruments collections and registering its identity.",
  },
  {
    zh: "设置控制体系",
    en: "Set up the control system",
    descZh: "把骨骼接到插件所驱动的控制器上，逐项核对映射关系。",
    descEn: "Wire the bones to the controllers the add-on drives and verify each mapping.",
  },
  {
    zh: "采集关键演奏姿势",
    en: "Capture key performance poses",
    descZh: "摆好并记录若干基准演奏姿势，作为生成动画时的插值骨架。",
    descEn: "Pose and record a set of baseline performance poses that later interpolation is built on.",
  },
  {
    zh: "采集乐器关键几何信息",
    en: "Capture instrument geometry",
    descZh: "标定乐器位置、朝向与关键部位分布，让动作落到正确的空间位置。",
    descEn: "Calibrate the instrument's position, orientation and key features so motion lands in the right place.",
  },
  {
    zh: "导出人物数据",
    en: "Export the performer data",
    descZh: "把以上配置打包成一份人物数据文件，这是 Rust 端唯一需要认识的东西。",
    descEn: "Package the configuration into one performer data file — the only thing the Rust app needs.",
  },
  {
    zh: "在 Rust 端注册演奏者",
    en: "Register the performer in the Rust app",
    descZh: "把人物数据放进程序目录并登记，使其出现在角色列表中并可被选择。",
    descEn: "Place the data in the app's folder and register it so it appears in the performer list.",
  },
];

function renderFlow(): void {
  const isZh = getLang() === "zh";
  const root = document.getElementById("advanced-flow-list");
  if (!root) return;
  root.innerHTML = FLOW.map(
    (s, i) => `<li class="tut-step">
  <span class="tut-step-no">${i + 1}</span>
  <div class="tut-step-body"><strong>${escapeHtml(isZh ? s.zh : s.en)}</strong><p>${escapeHtml(
    isZh ? s.descZh : s.descEn,
  )}</p></div>
</li>`,
  ).join("");
}

function renderStaticText(): void {
  const isZh = getLang() === "zh";
  const title = document.getElementById("advanced-title");
  const sub = document.getElementById("advanced-sub");
  const chip = document.getElementById("advanced-chip");
  const chip2 = document.getElementById("advanced-chip-2");
  const flowTitle = document.getElementById("advanced-flow-title");
  const listTitle = document.getElementById("advanced-list-title");
  const listSub = document.getElementById("advanced-list-sub");
  const footer = document.getElementById("advanced-footer");

  if (title)
    title.textContent = isZh
      ? "进阶教程：自定义人物完整路径"
      : "Advanced: Full Custom Performer Pathway";
  if (sub)
    sub.textContent = isZh
      ? "这一段讲的是把「你自己的人物」变成 Rust 端可以驱动的演奏者：骨骼预处理、实例初始化、控制体系、关键姿势、乐器几何、数据导出，最后在 Rust 端注册。自定义角色属于完整版功能，工作量与排错面也都明显大于 Quick Start。"
      : "This section turns your own character into a performer the Rust app can drive: rig prep, instance setup, control system, key poses, instrument geometry, data export and finally registration. Custom performers are a full-version feature, and the work and debugging surface are considerably heavier than Quick Start.";
  if (chip)
    chip.textContent = isZh ? "建议先完成 Quick Start" : "Finish Quick Start first";
  if (chip2) chip2.textContent = isZh ? "按乐器学习" : "Learn by instrument";
  if (flowTitle)
    flowTitle.textContent = isZh
      ? "自定义人物的七个阶段"
      : "The seven stages of a custom performer";
  if (listTitle)
    listTitle.textContent = isZh
      ? "按乐器进入进阶教程"
      : "Enter advanced tutorials by instrument";
  if (listSub)
    listSub.textContent = isZh
      ? "每个乐器的进阶教程都按上面七个阶段组织，页内右侧带大纲导航，页尾附 B 站演示视频。当前各页已搭好结构与占位，具体图文由作者补充。"
      : "Each instrument's advanced tutorial follows the seven stages above, with an on-page outline sidebar and a YouTube demo at the end. Structure and placeholders are in place; the author fills in the details.";
  if (footer)
    footer.textContent = isZh
      ? "复杂步骤建议对照 B 站视频逐步执行，不建议跳步。"
      : "For complex workflows, follow the YouTube videos step by step without skipping.";
}

function main(): void {
  initI18n();
  renderStaticText();
  renderFlow();
  render();
  document.addEventListener("langchange", () => {
    renderStaticText();
    renderFlow();
    render();
  });
}

main();
